export type PhaseId = 'site' | 'foundation' | 'structure' | 'enclosure' | 'finish' | 'built';
export const phases = [
  {
    id: 'site',
    number: '01',
    name: 'Site',
    headline: 'Start with the ground.',
    body: 'Before building begins, the site needs to be understood. Surveying, access, ground conditions, and drainage all inform the work that follows.',
    detail: 'Survey · Setting out · Site preparation',
  },
  {
    id: 'foundation',
    number: '02',
    name: 'Foundation',
    headline: 'Make the base sound.',
    body: 'Footings and the ground floor establish a stable base. Reinforcement, service routes, and waterproofing are checked before they become hidden work.',
    detail: 'Footings · Reinforcement · Ground floor',
  },
  {
    id: 'structure',
    number: '03',
    name: 'Structure',
    headline: 'Give the spaces their shape.',
    body: 'The ground-floor frame rises first, followed by the upper floor and roof structure. The stair opening, connections, and bracing are coordinated before the frame is enclosed.',
    detail: 'Ground floor · Upper floor · Roof structure',
  },
  {
    id: 'enclosure',
    number: '04',
    name: 'Enclosure',
    headline: 'Close out the weather.',
    body: 'Walls, roof, and glazing form a continuous envelope. Insulation, weather barriers, and careful junctions help keep the interior comfortable and dry.',
    detail: 'Roof · Walls · Windows and doors',
  },
  {
    id: 'finish',
    number: '05',
    name: 'Finish',
    headline: 'Bring the details together.',
    body: 'Joinery, stonework, and lighting complete the rooms. Outside, the terraces take shape and the pool shell is finished before planting and filling begin.',
    detail: 'Interior finishes · Terraces · Pool shell',
  },
  {
    id: 'built',
    number: '06',
    name: 'Built',
    headline: 'Ready for everyday life.',
    body: 'Lawns and trees soften the grounds, and the pool is filled for testing. The finished home opens onto a front garden and a private rear terrace, ready for final checks and handover.',
    detail: 'Planting · Pool commissioning · Handover',
  },
] as const;
export const materials = [
  {
    id: 'concrete',
    name: 'Concrete',
    spec: 'Board-formed concrete',
    detail:
      'The timber formwork leaves a quiet horizontal texture. Consistent board widths and carefully planned joints give the wall its rhythm.',
    use: 'Used in the foundations and floor slabs.',
    code: '01',
  },
  {
    id: 'steel',
    name: 'Steel',
    spec: 'Dark bronze finish',
    detail:
      'Steel columns and beams carry the floors and roof. A dark protective finish makes the exposed metalwork recede behind the warmer timber and stone.',
    use: 'Used in the structural frame and external metalwork.',
    code: '02',
  },
  {
    id: 'glass',
    name: 'Glass',
    spec: 'Insulated glazing',
    detail:
      'Large panes connect the interior to the landscape. Frame depth, seals, shading, and the glazing specification all matter as much as the view.',
    use: 'Used in the front entrance and garden-facing windows.',
    code: '03',
  },
  {
    id: 'timber',
    name: 'Timber',
    spec: 'Natural cedar',
    detail:
      'Narrow cedar boards bring warmth to the sheltered ceiling. Straight runs, neat ends, and space for natural movement keep the detail simple.',
    use: 'Used beneath the roof and in selected finishes.',
    code: '04',
  },
  {
    id: 'stone',
    name: 'Stone',
    spec: 'Honed limestone',
    detail:
      'A matte surface and restrained joint pattern give the terrace a calm, continuous feel. External stone needs the right slip resistance and drainage.',
    use: 'Used on the terrace and entrance steps.',
    code: '05',
  },
] as const;
export type MaterialId = (typeof materials)[number]['id'];
export const projects = [
  {
    slug: 'ridge-house',
    number: '01',
    name: 'Ridge House',
    type: 'Residential',
    image: '/images/estate-front.jpg',
    alt: 'A two-storey limestone and cream-stucco estate with charcoal slate roofs, a grand arched entrance, and a three-car garage beside a landscaped arrival court.',
    location: 'Woodland setting',
    area: 'Two-storey estate',
    year: 'Concept study',
    status: 'Concept study',
    scope: 'Custom residence',
    concept: 'A considered arrival. A private retreat.',
    description:
      'A European-influenced estate arranged around a strong central entrance, coordinated residential wings, and an integrated three-car garage. Beyond the house, terraces and a pool open onto a private garden.',
    approach:
      'Cream stucco, gray stone, limestone details, and charcoal slate bring the elevations together. Dark walnut and marble continue the restrained palette inside, with shared rooms on the ground floor and private suites above.',
    consideration:
      'The construction study follows the estate from its foundations to the finished landscape. The two floors, stair opening, doorways, and guided interior routes are coordinated with one shared architectural plan.',
    palette: ['Cream stucco', 'Limestone', 'Charcoal slate', 'Dark walnut'],
  },
  {
    slug: 'black-house',
    number: '02',
    name: 'The Black House',
    type: 'Residential',
    image: '/images/black-house.webp',
    alt: 'A charred-timber courtyard house around a fern garden, with warm timber rooms and rain-darkened stepping stones.',
    location: 'Sheltered courtyard',
    area: 'Courtyard residence',
    year: 'Concept study',
    status: 'Concept study',
    scope: 'Custom residence',
    concept: 'Privacy, with room for light.',
    description:
      'A dark timber house wrapped around a planted courtyard. The plan turns inward, creating a private garden that can be seen from the main living spaces.',
    approach:
      'Charred vertical timber gives the exterior a clear, quiet identity. Inside, natural wood softens the palette. Glazing connects the two without making every room feel exposed.',
    consideration:
      'The courtyard needs positive drainage away from the building, accessible thresholds, and planting that can thrive in partial shade. The timber cladding would be detailed as a ventilated rainscreen.',
    palette: ['Charred timber', 'Natural wood', 'Concrete plinth', 'Stone paving'],
  },
  {
    slug: 'forge-17',
    number: '03',
    name: 'Forge 17',
    type: 'Adaptive reuse',
    image: '/images/forge-17.webp',
    alt: 'An old brick workshop converted into a bright workspace with exposed steel trusses, an oak table, and olive lounge chairs.',
    location: 'Former industrial workshop',
    area: 'Workshop conversion',
    year: 'Concept study',
    status: 'Concept study',
    scope: 'Adaptive reuse',
    concept: 'A useful second life.',
    description:
      'An old brick workshop reimagined as a shared workspace. Existing brick walls, steel roof trusses, and tall windows remain the defining features of the interior.',
    approach:
      'New elements are kept simple and distinct from the original shell. A long oak table provides a shared working surface, with built-in storage and a small informal seating area alongside.',
    consideration:
      'Before an existing building is adapted, its structure, moisture conditions, fire strategy, and accessibility need assessment. New services would be coordinated around the retained frame.',
    palette: ['Existing brick', 'Exposed steel', 'Oak joinery', 'Polished concrete'],
  },
  {
    slug: 'monolith-hq',
    number: '04',
    name: 'Monolith HQ',
    type: 'Commercial',
    image: '/images/forge-17.webp',
    alt: 'A tactile brick and oak workplace interior with a communal table and exposed roof structure.',
    location: 'Industrial workspace setting',
    area: 'Workplace planning study',
    year: 'Concept study',
    status: 'Concept study',
    scope: 'Commercial interior',
    concept: 'Space to work, together.',
    description:
      'A companion workplace study exploring how a simple industrial shell can support focused work, shared tables, and informal conversation.',
    approach:
      'A clear circulation route separates movement from working areas. Storage is built into the perimeter, leaving the center of the room flexible. This study shares the Forge 17 visual and material direction.',
    consideration:
      'A developed scheme would need acoustic treatment, accessible circulation, coordinated power and data, and a lighting plan suited to different tasks.',
    palette: ['Retained brick', 'Oak furniture', 'Dark metal', 'Acoustic finishes'],
  },
] as const;
export const capabilities = [
  {
    number: '01',
    name: 'Custom homes',
    text: 'Homes shaped around the site, the design, and the people who will live there.',
    detail:
      'A custom home requires hundreds of connected decisions. Early coordination between the owner, architect, engineers, and builder helps turn the drawings into a clear, buildable scope.',
    items: ['Preconstruction planning', 'New residential construction', 'Interior and exterior detailing'],
  },
  {
    number: '02',
    name: 'Renovation & reuse',
    text: 'Thoughtful changes that make existing buildings useful for their next chapter.',
    detail:
      'Working with an existing building starts with understanding what is already there. Surveys, investigations, and realistic allowances help manage the unknowns before work begins.',
    items: ['Existing-condition reviews', 'Residential renovations', 'Adaptive reuse and fit-outs'],
  },
  {
    number: '03',
    name: 'Commercial spaces',
    text: 'Workplaces and interiors that balance character with everyday performance.',
    detail:
      'A commercial space needs to work for its occupants from day one. Access, durability, building services, and operational needs are considered alongside the architectural intent.',
    items: ['Workplace interiors', 'Commercial fit-outs', 'Joinery and finish coordination'],
  },
  {
    number: '04',
    name: 'Construction management',
    text: 'A clear view of the scope, the sequence, and the decisions ahead.',
    detail:
      'A coordinated program gives everyone a shared picture of the work. Cost reviews, procurement planning, and regular site communication keep decisions connected to their impact on the project.',
    items: ['Scope and cost planning', 'Trade and schedule coordination', 'Quality checks and handover'],
  },
] as const;
export const processSteps = phases.map((p) => [p.number, p.name, p.body]);
